import { IWorkFlowDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import { WorkFlowService } from "../../../services/workflow-service";
import { getSdInstance } from "../../../shared/utility/designer-context";
import { KuikaListTableRowAction } from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { isProcessActive } from "./create-workflow-processes";

/**
 * What can be done to a process from a list of them. The landing table and the editor's process
 * switcher share this list, and the two writes behind it, so the menus cannot offer different
 * things and the writes cannot differ in what they clean up.
 */

export interface IProcessRowActionHandlers {
  /** Only the landing renames inline; the switcher's bar has its own way to rename. */
  onRename?: (process: IWorkFlowDto) => void;
  onToggleActive: (process: IWorkFlowDto) => void;
  /** Opens the host's confirmation; nothing is deleted until it is answered. */
  onRequestDelete: (process: IWorkFlowDto) => void;
}

export const buildProcessRowActions = (
  handlers: IProcessRowActionHandlers
): KuikaListTableRowAction<IWorkFlowDto>[] => [
  {
    key: "rename",
    label: "Rename",
    icon: <KuikaSvg name="edit" width={14} height={14} color="var(--dark-7)" />,
    hidden: (): boolean => !handlers.onRename,
    onSelect: (row: IWorkFlowDto) => handlers.onRename?.(row)
  },
  {
    key: "activate",
    label: "Activate",
    icon: <KuikaSvg name="player-play" width={14} height={14} color="var(--dark-7)" />,
    hidden: (row: IWorkFlowDto): boolean => isProcessActive(row),
    onSelect: handlers.onToggleActive
  },
  {
    key: "deactivate",
    label: "Deactive",
    icon: <KuikaSvg name="ban" width={14} height={14} color="var(--dark-7)" />,
    hidden: (row: IWorkFlowDto): boolean => !isProcessActive(row),
    onSelect: handlers.onToggleActive
  },
  {
    key: "delete",
    label: "Delete",
    danger: true,
    icon: <KuikaSvg name="trash" width={14} height={14} />,
    onSelect: handlers.onRequestDelete
  }
];

/** Flips a process between active and inactive. */
export const toggleProcessActive = async (process: IWorkFlowDto): Promise<void> => {
  if (isProcessActive(process)) {
    await WorkFlowService.Inactivate(process.id);
  } else {
    await WorkFlowService.Activate(process.id);
  }
};

/**
 * Deletes a process and everything that points at it: the project's step-screen mappings, the
 * start-process trigger sync, and — when it is the open one — the editor's selection.
 */
export const deleteProcessEverywhere = async (sd: IScreenDesignContextState, target: IWorkFlowDto): Promise<void> => {
  await WorkFlowService.Delete(target.id);
  const project = sd.project;
  if (project?.processStepScreenIds?.length) {
    let hasMappingToDelete: boolean = false;
    for (let i = project.processStepScreenIds.length - 1; i >= 0; i--) {
      if (project.processStepScreenIds[i].processId === target.id) {
        project.processStepScreenIds.splice(i, 1);
        hasMappingToDelete = true;
      }
    }
    if (hasMappingToDelete) sd.updateProject(project);
  }
  getSdInstance().startProcessTriggerSync.pruneWorkflow(target.id);
  if (sd.lastOpenWfId === target.id) {
    sd.clearLastOpenedWfId();
    sd.clearSelectedWf();
  }
};
